import { useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { retrieveLocationList } from "@/Common/api/location/queries";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { actions } from "@/redux/reducers/Location";
import { groupFranchisesByOems } from "@/Common/helpers/locations";

function useLocations(tenant?: string) {
	const locations = useSelector((state: RootState) => state.location.locations);
	const dispatch = useDispatch();

	const { isLoading, refetch } = useQuery({
		queryKey: ["locations", tenant],
		queryFn: async () => {
			const locationList = await retrieveLocationList(tenant);
			if (locationList) {
				const mappedLocations = locationList?.map((location) => {
					const { oems } = location;
					const franchises = groupFranchisesByOems(oems);
					return { ...location, franchises };
				});
				dispatch(actions.SET_LOCATIONS({ locations: mappedLocations }));
				return locationList;
			}
			return [];
		},
		enabled: !!tenant,
		staleTime: Infinity,
	});

	const globalLocations: () => typeof locations = useCallback(() => {
		if (tenant && locations) {
			const isPresent = locations.find((element) => element.locationId === tenant);

			if (!isPresent) {
				return [
					...locations,
					{
						_id: `global${tenant}`,
						locationId: tenant,
						logo: "",
						name: "Global",
						prefix: "",
					} as (typeof locations)[number],
				];
			}
		}

		return locations;
	}, [tenant, locations]);

	return {
		locations,
		isLoading,
		refetch,
		globalLocations: globalLocations(),
	};
}

export { useLocations };
